import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { COMMON_TRADES } from "@pph-safety/shared";

const API_BASE = import.meta.env.VITE_API_URL || "";

interface InviteInfo {
  projectName: string;
  orgName: string;
  expiresAt: string;
  companyName: string | null;
  email: string | null;
}

export const Route = createFileRoute("/invite")({
  component: InviteLandingPage,
});

function InviteLandingPage() {
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(null);
  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [trade, setTrade] = useState("");
  const [customTrade, setCustomTrade] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Extract token from URL fragment
  useEffect(() => {
    const hash = window.location.hash;
    const tokenMatch = hash.match(/token=([^&]+)/);
    if (tokenMatch) {
      setToken(tokenMatch[1]);
    } else {
      setError("Invalid invite link. Please check the link and try again.");
      setLoading(false);
    }
  }, []);

  // Fetch invite info when token is available
  useEffect(() => {
    if (!token) return;

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/invitations/info/${token}`);
        if (res.status === 404) {
          setError("This invite link is not valid.");
          return;
        }
        if (res.status === 410) {
          setError(
            "This invite has expired. Please contact the general contractor for a new invite."
          );
          return;
        }
        if (res.status === 409) {
          setError("This invite has already been used.");
          return;
        }
        if (!res.ok) {
          setError("Unable to load invite. Please try again.");
          return;
        }

        const json = (await res.json()) as { data: InviteInfo };
        setInviteInfo(json.data);
        if (json.data.email) setEmail(json.data.email);
        if (json.data.companyName) setCompanyName(json.data.companyName);
      } catch {
        setError("Unable to reach the server. Please check your connection.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const finalTrade = trade === "Other" ? customTrade : trade;

    if (!name.trim()) return setFormError("Name is required");
    if (!email.trim()) return setFormError("Email is required");
    if (!companyName.trim()) return setFormError("Company name is required");
    if (!finalTrade.trim()) return setFormError("Trade is required");
    if (password.length < 8)
      return setFormError("Password must be at least 8 characters");
    if (password !== confirmPassword)
      return setFormError("Passwords do not match");

    setSubmitting(true);

    try {
      const res = await fetch(`${API_BASE}/api/invitations/accept`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          name: name.trim(),
          email: email.trim(),
          companyName: companyName.trim(),
          trade: finalTrade.trim(),
          password,
        }),
      });

      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string | Record<string, string[]>;
        };
        const msg =
          typeof body.error === "string"
            ? body.error
            : "Failed to create account. Please try again.";
        setFormError(msg);
        return;
      }

      // Success — session cookie is set, redirect to home
      navigate({ to: "/" });
    } catch {
      setFormError("Unable to reach the server. Please check your connection.");
    } finally {
      setSubmitting(false);
    }
  };

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="bg-paris-red px-6 py-4">
          <img
            src="/paris-logo-black.png"
            alt="Paris Mechanical"
            className="h-10 object-contain"
          />
        </div>
        <div className="flex-1 flex flex-col items-center justify-center px-6 bg-surface-base">
          <div className="max-w-sm text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-text-primary mb-2">
              Invite Issue
            </h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <p className="text-sm text-gray-400">
              Contact the general contractor for a new invite link.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading || !inviteInfo) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="bg-paris-red px-6 py-4">
          <img
            src="/paris-logo-black.png"
            alt="Paris Mechanical"
            className="h-10 object-contain"
          />
        </div>
        <div className="flex-1 flex items-center justify-center bg-surface-base">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-paris-red border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-500">Loading invite...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Branding Panel */}
      <div className="bg-paris-red flex flex-col items-center justify-center px-8 py-12 md:w-1/2 md:min-h-screen">
        <div className="text-center text-white max-w-md">
          <img
            src="/paris-logo-black.png"
            alt="Paris Mechanical"
            className="w-60 md:w-80 mx-auto mb-8 object-contain"
          />
          <p className="text-lg text-white/80 font-light mb-2">
            You've been invited to
          </p>
          <h1 className="text-3xl font-bold mb-4">{inviteInfo.projectName}</h1>
          <p className="text-sm text-white/60">by {inviteInfo.orgName}</p>
          <div className="mt-6 w-16 h-0.5 bg-white/30 mx-auto" />
          <p className="mt-6 text-sm text-white/60 leading-relaxed">
            FLHAs (Field Level Hazard Assessments) help keep everyone safe on
            the job site. Create your account to get started.
          </p>
        </div>
      </div>

      {/* Signup Form Panel */}
      <div className="flex flex-1 items-start md:items-center justify-center px-6 py-8 md:py-12 bg-surface-base">
        <div className="w-full max-w-sm">
          <div className="mb-6 text-center">
            <h2 className="text-2xl font-semibold tracking-tight">
              Create Your Account
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Fill out the form below to get started
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full h-12 px-3 rounded-md border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-pph-red focus:border-transparent"
                placeholder="John Smith"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                readOnly={!!inviteInfo.email}
                className={`w-full h-12 px-3 rounded-md border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-pph-red focus:border-transparent ${
                  inviteInfo.email ? "bg-gray-100 text-gray-500" : ""
                }`}
                placeholder="john@company.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Name
              </label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                required
                className="w-full h-12 px-3 rounded-md border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-pph-red focus:border-transparent"
                placeholder="ABC Electrical Ltd."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Trade
              </label>
              <select
                value={trade}
                onChange={(e) => setTrade(e.target.value)}
                required
                className="w-full h-12 px-3 rounded-md border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-pph-red focus:border-transparent"
              >
                <option value="">Select your trade</option>
                {COMMON_TRADES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              {trade === "Other" && (
                <input
                  type="text"
                  value={customTrade}
                  onChange={(e) => setCustomTrade(e.target.value)}
                  required
                  className="w-full h-12 px-3 mt-2 rounded-md border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-pph-red focus:border-transparent"
                  placeholder="Enter your trade"
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="w-full h-12 px-3 rounded-md border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-pph-red focus:border-transparent"
                placeholder="At least 8 characters"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full h-12 px-3 rounded-md border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-pph-red focus:border-transparent"
                placeholder="Re-enter your password"
              />
            </div>

            {formError && (
              <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-md">
                {formError}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full h-12 bg-paris-red text-white font-medium rounded-md hover:bg-paris-red/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating Account...
                </>
              ) : (
                "Create Account & Get Started"
              )}
            </button>
          </form>

          <p className="text-xs text-gray-400 text-center mt-4">
            Already have an account?{" "}
            <a href="/login" className="text-paris-red-text hover:underline">
              Sign in
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
